/**
 * Maps the stable error codes raised by `accept_company_invitation` onto
 * plain-language messages. Never surface the raw code to a user.
 */
const JOIN_ERROR_MESSAGES: Record<string, { title: string; description: string }> = {
  invitation_not_authenticated: {
    title: 'Please sign in again',
    description: 'Your session expired before the invitation could be accepted.',
  },
  invitation_not_found: {
    title: 'That invitation is no longer available',
    description: 'Ask the team to send you a fresh invitation.',
  },
  invitation_not_pending: {
    title: 'That invitation was already used',
    description: 'It has already been accepted or declined. Ask the team to send a new one.',
  },
  invitation_expired: {
    title: 'That invitation has expired',
    description: 'Ask an admin on the team to invite you again.',
  },
  invitation_email_unverified: {
    title: 'Please confirm your email first',
    description: 'Open the confirmation email we sent you, or sign in with Google or Microsoft, then accept the invitation.',
  },
  invitation_email_mismatch: {
    title: 'This invitation is for a different email',
    description: 'Sign in with the email address the invitation was sent to, or ask for a new one.',
  },
  company_not_absorbable: {
    title: "Your workspace can't be merged automatically",
    description:
      'It already holds inventory or messages, so an admin on the team has to move you across. Use “Request to join” and they can approve it.',
  },
};

export function describeJoinError(err: unknown): { title: string; description: string } {
  const raw =
    typeof err === 'object' && err !== null && 'message' in err
      ? String((err as { message?: unknown }).message ?? '')
      : '';

  for (const [code, message] of Object.entries(JOIN_ERROR_MESSAGES)) {
    if (raw.includes(code)) return message;
  }

  return {
    title: "Couldn't join the team",
    description: 'Please try again, or contact support if it keeps happening.',
  };
}
